import type { VaultNewRecordsByTable } from '../newRecords/types';

/** The vault table names the vault pages pass to `useNewRecordPills` / `newRecordsTable`. */
export const NEW_RECORD_TABLE = {
  medicalLicenses: 'medical_licenses',
  deaLicenses: 'dea_licenses',
  cdsLicenses: 'cds_licenses',
  boardCertifications: 'board_certifications',
  certifications: 'certifications',
  education: 'education',
  training: 'training',
  employment: 'employment',
  cmeTrainings: 'cme_trainings',
  privileges: 'provider_privileges',
} as const;

/**
 * [MCV-1812] The vault tables each dashboard card summarizes, keyed by the card key from
 * useDashboardSummary. A card lights up for exactly the records its page marks New.
 *
 * Documents has no entry: a parse creates records in the other tables, never a document. The
 * Documents card lights from `hasUploadToday` instead (a document uploaded today).
 */
export const CARD_NEW_RECORD_TABLES: Readonly<Record<string, readonly string[]>> = {
  medicalLicenses: [NEW_RECORD_TABLE.medicalLicenses],
  deaCdsLicenses: [NEW_RECORD_TABLE.deaLicenses, NEW_RECORD_TABLE.cdsLicenses],
  boardCerts: [NEW_RECORD_TABLE.boardCertifications],
  certifications: [NEW_RECORD_TABLE.certifications],
  education: [NEW_RECORD_TABLE.education, NEW_RECORD_TABLE.training],
  employment: [NEW_RECORD_TABLE.employment],
  cmeTraining: [NEW_RECORD_TABLE.cmeTrainings],
  privileges: [NEW_RECORD_TABLE.privileges],
};

/** The ids of the rows that have one, as numbers. */
export const idsOf = (rows: ReadonlyArray<{ id?: unknown }> | null | undefined): number[] =>
  (rows ?? []).filter((row) => row.id != null).map((row) => Number(row.id));

/**
 * The tables of a card that have an unseen parse-created record among the records the dashboard
 * loaded for that table (`recordIdsByTable`, per table because ids repeat across tables), in the
 * card's table order. The API still lists the unseen mark of a record deleted before the provider
 * saw it; the section never renders that row, so it could never be cleared, and a mark without a
 * loaded row must not light the card.
 */
export const newRecordTablesFor = (
  cardKey: string,
  byTable: VaultNewRecordsByTable,
  recordIdsByTable: Readonly<Record<string, readonly number[]>> | undefined
): string[] =>
  (CARD_NEW_RECORD_TABLES[cardKey] ?? []).filter((table) => {
    const unseen = byTable[table];
    const loaded = recordIdsByTable?.[table];
    if (!unseen?.length || !loaded?.length) return false;
    const loadedIds = new Set(loaded);
    return unseen.some((id) => loadedIds.has(id));
  });

/** True when the card has a new loaded record in any of its tables. */
export const cardHasNewRecords = (
  cardKey: string,
  byTable: VaultNewRecordsByTable,
  recordIdsByTable: Readonly<Record<string, readonly number[]>> | undefined
): boolean => newRecordTablesFor(cardKey, byTable, recordIdsByTable).length > 0;

/** The tab (URL hash) each table of a combined card lives on; the pages read these hashes. */
const TABLE_TAB_HASH: Readonly<Record<string, string>> = {
  [NEW_RECORD_TABLE.deaLicenses]: 'dea',
  [NEW_RECORD_TABLE.cdsLicenses]: 'cds',
  [NEW_RECORD_TABLE.education]: 'education',
  [NEW_RECORD_TABLE.training]: 'training',
};

/**
 * Where a lit card links. A combined card's page mounts only its active tab, and only the mounted
 * tab marks its rows seen, so the card opens the tab of its first table with new records (CDS or
 * Training when only those are new). Any other card keeps its link.
 */
export const newRecordsLink = (navigateTo: string, newTables: readonly string[]): string => {
  const hash = newTables.length > 0 ? TABLE_TAB_HASH[newTables[0]] : undefined;
  const hashAt = navigateTo.indexOf('#');
  if (!hash || hashAt === -1) return navigateTo;
  return `${navigateTo.slice(0, hashAt)}#${hash}`;
};
